import apiClient from './api'
import type { EmailThread, EmailMessage, EmailFilters, PaginatedResponse, GmailIntegration } from '../types'

export const gmailService = {
  // Check Gmail connection status
  async getConnectionStatus(): Promise<GmailIntegration> {
    const response = await apiClient.get('/gmail/status')
    return response.data
  },

  // List email threads
  async listThreads(params: EmailFilters & { page?: number; limit?: number; pageToken?: string }) {
    const response = await apiClient.get<PaginatedResponse<EmailThread>>('/gmail/threads', { params })
    return response.data
  },

  // Get single email thread (or all threads from sender if senderEmail provided)
  async getThread(threadId: string, senderEmail?: string) {
    const params = senderEmail ? { sender: senderEmail } : {}
    const response = await apiClient.get<{ metadata: EmailThread; messages: EmailMessage[] }>(
      `/gmail/threads/${threadId}`,
      { params }
    )
    return response.data
  },

  // Send new email
  async sendEmail(data: {
    to: string
    subject: string
    body: string
    threadId?: string
    inReplyTo?: string
  }) {
    const response = await apiClient.post('/gmail/send', data)
    return response.data
  },

  // Mark thread as read/unread
  async markAsRead(threadId: string, read: boolean = true) {
    const response = await apiClient.put(`/gmail/threads/${threadId}`, {
      isUnread: !read
    })
    return response.data
  },

  // Pin/unpin thread
  async pinThread(threadId: string, isPinned: boolean) {
    const response = await apiClient.put(`/gmail/threads/${threadId}/pin`, {
      isPinned
    })
    return response.data
  },

  // Add/remove labels
  async updateLabels(threadId: string, labels: { add?: string[]; remove?: string[] }) {
    const response = await apiClient.put(`/gmail/threads/${threadId}/labels`, labels)
    return response.data
  },

  // Delete thread (move to trash)
  async deleteThread(threadId: string) {
    const response = await apiClient.delete(`/gmail/threads/${threadId}`)
    return response.data
  },

  // Search emails
  async searchEmails(query: string, params?: { limit?: number; pageToken?: string }) {
    const response = await apiClient.get<PaginatedResponse<EmailThread>>('/gmail/search', {
      params: { q: query, ...params }
    })
    return response.data
  },

  // Get labels
  async getLabels() {
    const response = await apiClient.get('/gmail/labels')
    return response.data?.data || []
  },

  // Sync emails manually
  async syncEmails() {
    const response = await apiClient.post('/gmail/sync')
    return response.data
  },

  // Get email analytics
  async getAnalytics() {
    const response = await apiClient.get('/gmail/analytics')
    return response.data
  },

  // Extract customer data from email using AI
  async extractCustomer(threadId: string, senderEmail?: string) {
    const params = senderEmail ? { sender: senderEmail } : {}
    const response = await apiClient.post(`/gmail/threads/${threadId}/extract-customer`, {}, { params })
    return response.data
  },

  // Create customer from extracted data
  async createCustomer(threadId: string, customerData: any) {
    const response = await apiClient.post(`/gmail/threads/${threadId}/create-customer`, customerData)
    return response.data
  },

  // Extract estimate data from email using AI
  async extractEstimate(threadId: string, senderEmail?: string) {
    const params = senderEmail ? { sender: senderEmail } : {}
    const response = await apiClient.post(`/gmail/threads/${threadId}/extract-estimate`, {}, { params })
    return response.data
  },

  // Create estimate from extracted data
  async createEstimate(threadId: string, customerId: string, estimateData: any) {
    const response = await apiClient.post(`/gmail/threads/${threadId}/create-estimate`, {
      customerId,
      estimateData
    })
    return response.data
  },

  // Get price book items
  async getPriceBookItems(params?: { search?: string; category?: string; itemType?: string }) {
    const response = await apiClient.get('/gmail/price-book', { params })
    return response.data
  },

  // Re-link all existing email threads to customers
  async relinkCustomers() {
    const response = await apiClient.post('/gmail/relink-customers')
    return response.data
  },

  // Extract job data from email using AI
  async extractJob(threadId: string, senderEmail?: string) {
    const params = senderEmail ? { sender: senderEmail } : {}
    const response = await apiClient.post(`/gmail/threads/${threadId}/extract-job`, {}, { params })
    return response.data
  },

  // Create job from email
  async createJob(threadId: string, customerId: string, jobData: any) {
    const response = await apiClient.post(`/gmail/threads/${threadId}/create-job`, {
      customerId,
      jobData
    })
    return response.data
  },

  // Get customers for bulk email
  async getCustomersForBulkEmail(search?: string, limit?: number) {
    const params: any = {}
    if (search) params.search = search
    if (limit) params.limit = limit
    
    const response = await apiClient.get('/gmail/customers', { params })
    return response.data
  },

  // Send bulk email
  async bulkSendEmail(data: {
    customerIds?: string[]
    subject: string
    body: string
    sendToAll?: boolean
    enableTracking?: boolean
  }) {
    const response = await apiClient.post('/gmail/bulk-send', data)
    return response.data
  },
}

export default gmailService

